import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { SidebarProvider } from './contexts/SidebarContext';
import LoginPage from './pages/LoginPage';
import UserProfilePage from './pages/UserProfilePage';
import AdminPage from './pages/AdminPage';
import ProtectedRoute, { AdminRoute } from './components/ProtectedRoute';
import AITrainingDashboard from './components/ai/AITrainingDashboard';
import StandaloneRegisterPage from './pages/StandaloneRegisterPage';
import SecurityQuestionsSetupPage from './pages/SecurityQuestionsSetupPage';
import UnifiedStandaloneDashboard from './pages/UnifiedStandaloneDashboard';
import ImprovedCreaSchedaPage from './components/Standalone/ImprovedCreaSchedaPage';
import VisualizzaSchede from './components/Standalone/VisualizzaSchede';
import ImprovedVisualizzaSchede from './components/Standalone/ImprovedVisualizzaSchede';
import ModificaSchedaPage from './components/Standalone/ModificaSchedaPage';
import GymPage from './pages/GymPage';
import VersionChecker from './components/common/VersionChecker';
import EnvironmentBadge from './components/common/EnvironmentBadge';
import { getBasePath } from './config/basePath';
import { TRAINER_HOME_PATH } from './config/navigationConfig';

function RoleBasedRedirect({ children }) {
    const { currentUser, isAdmin, isGym, isTrainer } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (currentUser) {
            if (isAdmin() && location.pathname === '/') {
                navigate('/admin', { replace: true });
            } else if (isGym() && location.pathname === '/') {
                navigate('/gym', { replace: true });
            } else if (isTrainer() && location.pathname === '/') {
                navigate(TRAINER_HOME_PATH, { replace: true });
            } else if (currentUser.role_name === 'standalone' && location.pathname === '/') {
                navigate('/standalone/dashboard', { replace: true });
            }
        }
    }, [currentUser, isAdmin, isGym, isTrainer, navigate, location]);

    return <>{children}</>;
}

function AppRoutes() {
    return (
        <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/standalone-register" element={<StandaloneRegisterPage />} />
            <Route path="/security-questions-setup" element={<SecurityQuestionsSetupPage />} />

            {/* Route "/" gestita da RoleBasedRedirect nel layout principale */}
            <Route 
                path="/" 
                element={
                    <ProtectedRoute>
                        <RoleBasedRedirect>
                            <Navigate to="/standalone/dashboard" replace />
                        </RoleBasedRedirect>
                    </ProtectedRoute>
                } 
            />

            {/* Dashboard unificata standalone */}
            <Route 
                path="/standalone/dashboard" 
                element={
                    <ProtectedRoute>
                        <UnifiedStandaloneDashboard />
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/standalone/crea-scheda" 
                element={
                    <ProtectedRoute>
                        <ImprovedCreaSchedaPage />
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/standalone/visualizza-schede" 
                element={
                    <ProtectedRoute>
                        <ImprovedVisualizzaSchede />
                    </ProtectedRoute>
                }
            />

            <Route
                path="/standalone/modifica-scheda/:id"
                element={
                    <ProtectedRoute>
                        <ModificaSchedaPage />
                    </ProtectedRoute>
                }
            />
            
            <Route 
                path="/ai-training" 
                element={
                    <ProtectedRoute>
                        <AITrainingDashboard />
                    </ProtectedRoute>
                } 
            />

            <Route 
                path="/admin/*" 
                element={<AdminRoute><AdminPage /></AdminRoute>} 
            />

            <Route 
                path="/gym/*" 
                element={<ProtectedRoute><GymPage /></ProtectedRoute>} 
            />

            <Route path="/profile" element={<UserProfilePage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    const basePath = getBasePath();

    if (typeof window !== 'undefined' && !window.__FITGYM_BASEPATH_LOGGED__) {
        console.info('[FitGym debug] Router basename =', basePath);
        window.__FITGYM_BASEPATH_LOGGED__ = true;
    }

    return (
        <Router basename={basePath}>
            <ThemeProvider>
                <SidebarProvider>
                    <AuthProvider>
                        <NotificationProvider>
                            {/* Toast Notifications - Elegant notifications system */}
                            <Toaster
                                position="top-right"
                                toastOptions={{
                                    duration: 4000,
                                    style: {
                                        background: '#1f2937',
                                        color: '#fff',
                                        borderRadius: '0.75rem',
                                        padding: '16px',
                                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                                    },
                                    success: {
                                        iconTheme: {
                                            primary: '#10b981',
                                            secondary: '#fff',
                                        },
                                    },
                                    error: {
                                        iconTheme: {
                                            primary: '#ef4444',
                                            secondary: '#fff',
                                        },
                                    },
                                }}
                            />
                            <VersionChecker>
                                <AppRoutes />
                            </VersionChecker>
                            <EnvironmentBadge />
                        </NotificationProvider>
                    </AuthProvider>
                </SidebarProvider>
            </ThemeProvider>
        </Router>
    );
}

export default App;